import { Injectable } from '@angular/core';

import { AnalyticsEventCategory } from '../../../shared/configs/analytics-event-category.config';
import { AnalyticsEventTypes } from '../../../shared/configs/analytics-event-types.config';
import { FirestoreAnalyticsService } from '../firestore-analytics/firestore-analytics.service';
import { GoogleAnalyticsService } from '../google-analytics/google-analytics.service';

@Injectable({
  providedIn: 'root', // This makes the service available globally
})
export class AnalyticsService {
  constructor(
    private googleAnalyticsService: GoogleAnalyticsService,
    private firestoreAnalyticsService: FirestoreAnalyticsService,
  ) {}

  private sessionId?: string;

  sendPageViewEvent(
    pageName: string,
    timeSpent: number,
    bounce: boolean,
    params: Map<string, any> = new Map(),
  ): void {
    const currentSessionId = this.sessionId ?? '';
    this.firestoreAnalyticsService.sendPageViewEvent(
      pageName,
      timeSpent,
      bounce,
      params,
      currentSessionId,
    );
    this.googleAnalyticsService.sendPageViewEvent(pageName, timeSpent, bounce, params);
  }

  sendPageInteractionEvent(eventName: string, params: Map<string, any> = new Map()): void {
    const currentSessionId = this.sessionId ?? '';
    this.firestoreAnalyticsService.sendInteractionEvent(eventName, params, currentSessionId);
    this.googleAnalyticsService.sendPageInteractionEvent(eventName, params);
  }

  /**
   * Sends an event to both Google Analytics and Firestore.
   * @param eventType Type of the event
   * @param eventCategory Category of the event
   * @param eventLabel Label of the event
   * @param sessionId
   */
  sendEvent(
    eventType: AnalyticsEventTypes,
    eventCategory: AnalyticsEventCategory,
    eventLabel: string,
  ): void {
    // Send event to Google Analytics
    this.googleAnalyticsService.sendEvent(eventType, eventCategory, eventLabel);
    const currentSessionId = this.sessionId ?? '';
    // Send event to Firestore
    this.firestoreAnalyticsService.sendEvent(
      eventType,
      eventCategory,
      eventLabel,
      currentSessionId,
    );
  }

  /**
   * Sets the analytics configuration for both Google Analytics and Firestore.
   * @param pagePath The path of the page being viewed
   * @param sessionId
   */
  setAnalyticsConfig(pagePath: string, sessionId: string): void {
    this.sessionId = sessionId;
    // Set analytics configuration in Firestore
    this.firestoreAnalyticsService.setAnalyticsConfig(pagePath, sessionId);
  }
}
